import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { ensureUserAccount } from '../services/accountService';
import { forceCreateUserAccount, runAccountDiagnostics } from '../services/accountDiagnostics';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);
  const [accountDiagnostics, setAccountDiagnostics] = useState<any>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('Processing OAuth callback');
        setProcessing(true);

        // Check URL for OAuth state error
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('error')) {
          const errorDesc = urlParams.get('error_description') || urlParams.get('error');
          console.error('Error in OAuth callback URL:', errorDesc);
          setError(`Authentication error: ${errorDesc}`);
          setProcessing(false);
          return;
        }

        // Check for email confirmation with query parameters (token_hash + type)
        const tokenHash = urlParams.get('token_hash');
        const type = urlParams.get('type');

        if (tokenHash && type) {
          console.log('Email confirmation detected (query params):', { type });

          // For email confirmations, use verifyOtp
          if (type === 'signup' || type === 'email') {
            const { data, error } = await supabase.auth.verifyOtp({
              token_hash: tokenHash,
              type: type === 'signup' ? 'signup' : 'email'
            });

            if (error) {
              console.error('Email verification error:', error);
              setError(`Authentication error: ${error.message}`);
              setProcessing(false);
              return;
            }

            if (data?.session) {
              console.log('Email verified successfully, setting up account...');

              // Check for stored plan selection
              const selectedPlan = sessionStorage.getItem('selectedPlan');
              console.log('Retrieved selected plan from sessionStorage:', selectedPlan);

              // Ensure user account is created with selected plan
              await ensureUserAccount(data.session, selectedPlan || undefined);

              // Clear stored plan selection
              sessionStorage.removeItem('selectedPlan');
              sessionStorage.removeItem('selectedBillingCycle');

              // Redirect to orgs page to start Quick Start guide
              console.log('Email verified, redirecting to orgs page');
              window.history.replaceState({}, document.title, '/orgs');
              navigate('/orgs', { replace: true });
              return;
            }
          }
        }

        // Check for hash fragment with Supabase auth data (OAuth flow)
        if (window.location.hash) {
          console.log('Hash parameter detected, processing...');
          
          // Let Supabase handle everything with the built-in method
          const { data, error } = await supabase.auth.getSessionFromUrl();
          
          if (error) {
            console.error('Error getting session from URL:', error);
            setError(`Authentication error: ${error.message}`);
            setProcessing(false);
            return;
          }
          
          if (data?.session) {
            console.log('Successfully extracted session from URL');
            
            // Multi-tier account setup strategy
            try {
              console.log('Setting up account for user:', data.session.user.email);
              const userId = data.session.user.id;

              // Check for stored plan selection
              const selectedPlan = sessionStorage.getItem('selectedPlan');
              console.log('Retrieved selected plan from sessionStorage:', selectedPlan);

              // 1. Try the standard approach first
              const success = await ensureUserAccount(data.session, selectedPlan || undefined);

              if (success) {
                console.log('Account successfully set up for user');
                // Clear stored plan selection after successful account creation
                sessionStorage.removeItem('selectedPlan');
                sessionStorage.removeItem('selectedBillingCycle');
              } else {
                console.warn('Standard account setup failed, trying alternative methods');

                // 2. Try the direct Edge Function approach (using admin privileges)
                const forceResult = await forceCreateUserAccount(data.session);

                if (forceResult.success) {
                  console.log('Account set up using admin privileges');
                  // Clear stored plan selection after successful account creation
                  sessionStorage.removeItem('selectedPlan');
                  sessionStorage.removeItem('selectedBillingCycle');
                } else {
                  console.error('All account setup methods failed, running diagnostics');

                  // 3. If all else fails, run diagnostics to capture the problem
                  const diagResult = await runAccountDiagnostics(userId);
                  setAccountDiagnostics(diagResult.diagnostics);

                  if (!diagResult.success) {
                    console.error('Account setup failed with diagnostics:', diagResult.diagnostics);
                  }
                }
              }
            } catch (accountError) {
              console.error('Exception during account setup:', accountError);
              // Continue anyway - still allow the user to reach dashboard where they can retry
            }
            
            // Check for pending invitation first
            const pendingInvitation = sessionStorage.getItem('pendingInvitation');
            if (pendingInvitation) {
              console.log('Found pending invitation, redirecting to complete acceptance');
              // Don't remove from sessionStorage yet - let AcceptInvitationPage handle that
              window.history.replaceState({}, document.title, `/invitation/${pendingInvitation}`);
              navigate(`/invitation/${pendingInvitation}`, { replace: true });
              return;
            }
            
            // Check for pending transfer
            const pendingTransferToken = sessionStorage.getItem('pendingTransferToken');
            
            if (pendingTransferToken) {
              // Clean up and redirect to transfer acceptance
              sessionStorage.removeItem('pendingTransferToken');
              window.history.replaceState({}, document.title, '/accept-transfer');
              console.log('Authentication successful, redirecting to accept transfer');
              navigate(`/accept-transfer?token=${pendingTransferToken}`, { replace: true });
            } else {
              // Check for stored return URL from before OAuth redirect
              const authReturnUrl = sessionStorage.getItem('authReturnUrl');
              if (authReturnUrl) {
                sessionStorage.removeItem('authReturnUrl');
                const decodedUrl = decodeURIComponent(authReturnUrl);
                // Validate it's a relative URL for security
                if (decodedUrl.startsWith('/') && !decodedUrl.startsWith('//')) {
                  window.history.replaceState({}, document.title, decodedUrl);
                  console.log('Authentication successful, redirecting to return URL:', decodedUrl);
                  navigate(decodedUrl, { replace: true });
                  return;
                }
              }
              
              // Default: Clean up and redirect to orgs page to show Quick Start
              window.history.replaceState({}, document.title, '/orgs');
              console.log('Authentication successful, redirecting to orgs page');
              navigate('/orgs', { replace: true });
            }
            return;
          }
        }
        
        // If we get here, there was no hash or session - check manually
        const { data: sessionData } = await supabase.auth.getSession();
        
        if (sessionData?.session) {
          console.log('Found existing session, checking for pending transfer');
          
          const pendingTransferToken = sessionStorage.getItem('pendingTransferToken');
          if (pendingTransferToken) {
            sessionStorage.removeItem('pendingTransferToken');
            navigate(`/accept-transfer?token=${pendingTransferToken}`, { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        } else {
          console.error('No session found after OAuth callback');
          setError('Failed to authenticate. Please try again.');
          setProcessing(false);
        }
      } catch (err) {
        console.error('Error processing callback:', err);
        setError(`Authentication error: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setProcessing(false);
      }
    };

    handleCallback();
  }, [navigate]);

  if (processing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-gray-600">Setting up your account...</p>
      </div>
    );
  }

  if (error || accountDiagnostics) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white p-8 shadow-lg rounded-lg">
          <div className="flex flex-col items-center mb-6">
            <div className="text-2xl font-bold text-blue-600">FlowDocs</div>
            <h1 className="mt-4 text-xl font-semibold text-gray-800">
              {error ? 'Authentication Error' : 'Account Setup Issue'}
            </h1>
          </div>
          
          {error && (
            <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              <p className="font-bold">Error</p>
              <p>{error}</p>
            </div>
          )}
          
          {accountDiagnostics && (
            <div className="mt-4 p-4 bg-amber-100 border border-amber-400 text-amber-800 rounded">
              <p className="font-bold mb-2">Account Diagnostics</p>
              <ul className="text-sm">
                <li className="mb-1">
                  Connection: 
                  {accountDiagnostics.connectionSuccessful 
                    ? <span className="text-green-600 ml-1">✓</span> 
                    : <span className="text-red-600 ml-1">✗</span>}
                </li>
                <li className="mb-1">
                  Accounts Access: 
                  {accountDiagnostics.canReadAccounts 
                    ? <span className="text-green-600 ml-1">✓</span> 
                    : <span className="text-red-600 ml-1">✗</span>}
                </li>
                <li className="mb-1">
                  Roles Access: 
                  {accountDiagnostics.canReadRoles 
                    ? <span className="text-green-600 ml-1">✓</span> 
                    : <span className="text-red-600 ml-1">✗</span>}
                </li>
                <li>Accounts found: {accountDiagnostics.userAccountsFound}</li>
                <li>Roles found: {accountDiagnostics.userRolesFound}</li>
              </ul>
              <p className="mt-2 text-xs text-amber-800">Please contact support with this information.</p>
            </div>
          )}
          
          <div className="mt-6 space-y-3">
            <button 
              onClick={() => navigate('/dashboard')}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Continue to Dashboard
            </button>
            <button 
              onClick={() => navigate('/login')}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}